import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { httpsCallable, functions } from '@/lib/functions';
import { db } from '@/lib/firebase';
import { FREE_PLAN_LIMITS } from '@/types/entitlements';

export type UsageCounterKey =
  | 'ai_trip_recommendations' // "Plan with AI" on Trips screen (3/month for free)
  | 'ai_plan_idea_cards' // Plan tab chatPlanner suggestion cards / month (free)
  | 'ai_gap_activity_suggestions' // Fill your day AI picks / month (free)
  | 'ai_travel_companion_queries' // Travel companion queries / month (free)
  | 'email_sync_trip'; // booking import (1 trip lifetime for free)

const getMonthKeyUtc = (d = new Date()): string => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

export async function consumeMonthlyLimit(
  userId: string,
  key: UsageCounterKey,
  limitPerMonth: number,
  increment: number = 1,
): Promise<{ allowed: boolean; count: number; monthKey: string }> {
  if (!userId) {
    return { allowed: false, count: 0, monthKey: getMonthKeyUtc() };
  }

  const monthKey = getMonthKeyUtc();
  const inc = Math.min(50, Math.max(1, Math.floor(increment)));
  const callable = httpsCallable<
    { key: UsageCounterKey; scope: 'monthly'; limit: number; increment?: number },
    { allowed: boolean; count: number; monthKey?: string }
  >(functions, 'consumeUsageLimit');
  const result = await callable({
    key,
    scope: 'monthly',
    limit: limitPerMonth,
    increment: inc,
  });
  return {
    allowed: Boolean(result.data?.allowed),
    count: Number(result.data?.count ?? 0),
    monthKey: result.data?.monthKey || monthKey,
  };
}

/** Live view of free-plan Trips AI usage (read-only; writes stay on server). */
export type AiTripRecommendationsQuotaView = {
  limit: number;
  used: number;
  remaining: number;
  currentMonthKey: string;
};

export function subscribeAiTripRecommendationsQuota(
  userId: string,
  enabled: boolean,
  onNext: (q: AiTripRecommendationsQuotaView | null) => void,
): Unsubscribe {
  if (!enabled || !userId) {
    onNext(null);
    return () => {};
  }
  const ref = doc(db, 'users', userId, 'usageCounters', 'ai_trip_recommendations');
  return onSnapshot(
    ref,
    (snap) => {
      const currentMonthKey = getMonthKeyUtc();
      const data = snap.data();
      const docMonth = typeof data?.monthKey === 'string' ? data.monthKey : null;
      const count = typeof data?.count === 'number' ? data.count : 0;
      const used = docMonth === currentMonthKey ? count : 0;
      const limit = FREE_PLAN_LIMITS.aiTripRecommendationsPerMonth;
      onNext({
        limit,
        used,
        remaining: Math.max(0, limit - used),
        currentMonthKey,
      });
    },
    () => {
      onNext(null);
    },
  );
}

export type AiPlanIdeaCardsQuotaView = {
  limit: number;
  used: number;
  remaining: number;
  currentMonthKey: string;
};

/** Live view of free-plan Plan AI (chatPlanner) card usage. */
export function subscribeAiPlanIdeaCardsQuota(
  userId: string,
  enabled: boolean,
  onNext: (q: AiPlanIdeaCardsQuotaView | null) => void,
): Unsubscribe {
  if (!enabled || !userId) {
    onNext(null);
    return () => {};
  }
  const ref = doc(db, 'users', userId, 'usageCounters', 'ai_plan_idea_cards');
  return onSnapshot(
    ref,
    (snap) => {
      const currentMonthKey = getMonthKeyUtc();
      const data = snap.data();
      const docMonth = typeof data?.monthKey === 'string' ? data.monthKey : null;
      const count = typeof data?.count === 'number' ? data.count : 0;
      const used = docMonth === currentMonthKey ? count : 0;
      const limit = FREE_PLAN_LIMITS.aiPlanIdeaCardsPerMonth;
      onNext({
        limit,
        used,
        remaining: Math.max(0, limit - used),
        currentMonthKey,
      });
    },
    () => {
      onNext(null);
    },
  );
}

export async function consumeLifetimeLimit(
  userId: string,
  key: UsageCounterKey,
  limit: number,
): Promise<{ allowed: boolean; count: number }> {
  if (!userId) {
    return { allowed: false, count: 0 };
  }

  const callable = httpsCallable<
    { key: UsageCounterKey; scope: 'lifetime'; limit: number },
    { allowed: boolean; count: number }
  >(functions, 'consumeUsageLimit');
  const result = await callable({ key, scope: 'lifetime', limit });
  return {
    allowed: Boolean(result.data?.allowed),
    count: Number(result.data?.count ?? 0),
  };
}

